import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_ENDPOINTS, apiClient, TokenService } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const ROLES = {
  ADMIN: 'admin',
  HR_MANAGER: 'hr_manager',
  TRAINING_CURATOR: 'training_curator',
  USER: 'user'
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Администратор',
  [ROLES.HR_MANAGER]: 'HR-менеджер',
  [ROLES.TRAINING_CURATOR]: 'Куратор обучения',
  [ROLES.USER]: 'Пользователь'
};

export const PERMISSIONS = {
  SETTINGS_ACCESS: 'settings_access',
  SETTINGS_USERS_TAB: 'settings_users_tab',
  SETTINGS_MATRIX_TAB: 'settings_matrix_tab',
  SETTINGS_PROCESS_TAB: 'settings_process_tab',
  SETTINGS_TRAININGS_TAB: 'settings_trainings_tab',
  SETTINGS_POSITIONS_TAB: 'settings_positions_tab',
  SETTINGS_BRIGADES_TAB: 'settings_brigades_tab',
  SETTINGS_EMPLOYEES_TAB: 'settings_employees_tab',
  HEATMAP_ACCESS: 'heatmap_access',
  EVENTS_ACCESS: 'events_access',
  EVENTS_MANAGE: 'events_manage',
  USERS_MANAGE: 'users_manage',
  TRAININGS_MANAGE: 'trainings_manage',
  TRAININGS_ASSIGN: 'trainings_assign',
  TRAININGS_COMPLETE: 'trainings_complete',
  STATISTICS_VIEW: 'statistics_view',
  MATRIX_MANAGE: 'matrix_manage'
};

const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    PERMISSIONS.SETTINGS_ACCESS,
    PERMISSIONS.SETTINGS_USERS_TAB,
    PERMISSIONS.SETTINGS_MATRIX_TAB,
    PERMISSIONS.SETTINGS_PROCESS_TAB,
    PERMISSIONS.SETTINGS_TRAININGS_TAB,
    PERMISSIONS.SETTINGS_POSITIONS_TAB,
    PERMISSIONS.SETTINGS_BRIGADES_TAB,
    PERMISSIONS.SETTINGS_EMPLOYEES_TAB,
    PERMISSIONS.HEATMAP_ACCESS,
    PERMISSIONS.EVENTS_ACCESS,
    PERMISSIONS.EVENTS_MANAGE,
    PERMISSIONS.USERS_MANAGE,
    PERMISSIONS.TRAININGS_MANAGE,
    PERMISSIONS.TRAININGS_ASSIGN,
    PERMISSIONS.TRAININGS_COMPLETE,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.MATRIX_MANAGE
  ],
  [ROLES.HR_MANAGER]: [
    PERMISSIONS.SETTINGS_ACCESS,
    PERMISSIONS.SETTINGS_MATRIX_TAB,
    PERMISSIONS.SETTINGS_TRAININGS_TAB,
    PERMISSIONS.SETTINGS_POSITIONS_TAB,
    PERMISSIONS.SETTINGS_BRIGADES_TAB,
    PERMISSIONS.SETTINGS_EMPLOYEES_TAB,
    PERMISSIONS.HEATMAP_ACCESS,
    PERMISSIONS.EVENTS_ACCESS,
    PERMISSIONS.EVENTS_MANAGE,
    PERMISSIONS.TRAININGS_MANAGE,
    PERMISSIONS.TRAININGS_ASSIGN,
    PERMISSIONS.TRAININGS_COMPLETE,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.MATRIX_MANAGE
  ],
  [ROLES.TRAINING_CURATOR]: [
    PERMISSIONS.SETTINGS_ACCESS,
    PERMISSIONS.SETTINGS_MATRIX_TAB,
    PERMISSIONS.SETTINGS_TRAININGS_TAB,
    PERMISSIONS.SETTINGS_POSITIONS_TAB,
    PERMISSIONS.SETTINGS_BRIGADES_TAB,
    PERMISSIONS.SETTINGS_EMPLOYEES_TAB,
    PERMISSIONS.HEATMAP_ACCESS,
    PERMISSIONS.EVENTS_ACCESS,
    PERMISSIONS.EVENTS_MANAGE,
    PERMISSIONS.TRAININGS_MANAGE,
    PERMISSIONS.TRAININGS_ASSIGN,
    PERMISSIONS.TRAININGS_COMPLETE,
    PERMISSIONS.STATISTICS_VIEW,
    PERMISSIONS.MATRIX_MANAGE
  ],
  [ROLES.USER]: [
    PERMISSIONS.TRAININGS_COMPLETE
  ]
};

// Функция нормализации данных пользователя
const normalizeUserData = (userData) => {
  if (!userData) return null;
  
  let actualUserData = userData;
  
  if (userData.data && typeof userData.data === 'object') {
    actualUserData = userData.data;
  } else if (userData.user && typeof userData.user === 'object') {
    actualUserData = userData.user;
  }
  
  return {
    id: actualUserData.id,
    name: actualUserData.name || actualUserData.fullName || actualUserData.username || 'Пользователь',
    username: actualUserData.username || '',
    email: actualUserData.email || '',
    role: actualUserData.role || ROLES.USER,
    avatar: actualUserData.avatar || null,
    employee_id: actualUserData.employee_id || null,
    lastLogin: actualUserData.lastLogin || actualUserData.last_login || null,
    createdAt: actualUserData.createdAt || actualUserData.created_at || null
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      
      if (token) {
        try {
          console.log('[AuthProvider] Getting user data from /auth/me');
          
          const userData = await apiClient.get(API_ENDPOINTS.AUTH_ME);
          
          if (isMounted.current) {
            const normalizedUser = normalizeUserData(userData);
            setUser(normalizedUser);
            setLoading(false);
            console.log('[AuthProvider] User authenticated:', normalizedUser?.name);
          }
        } catch (err) {
          console.error('[AuthProvider] Error fetching /auth/me:', err);
          
          if (isMounted.current) {
            // Очищаем все токены при ошибке
            TokenService.removeTokens();
            localStorage.removeItem('user');
            sessionStorage.clear();
            
            setUser(null);
            setError('Сессия истекла. Пожалуйста, войдите снова.');
            setLoading(false);
          }
        }
      } else {
        // Нет токена - очищаем всё
        localStorage.removeItem('user');
        setUser(null);
        setLoading(false);
        console.log('[AuthProvider] No token found, user not authenticated');
      }
    };

    initAuth();

    return () => {
      isMounted.current = false;
    };
  }, []); // Пустой массив зависимостей

  const login = useCallback(async (username, password, rememberMe = false) => {
    setError(null);
    setLoading(true);
    
    try {
      console.log('[AuthProvider] Logging in user:', username);
      
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        username,
        password
      });
      
      let access_token, refresh_token, userData;
      
      if (response.access_token) {
        access_token = response.access_token;
        refresh_token = response.refresh_token;
        userData = response.user || response.data?.user || response.data;
      } else if (response.data) {
        access_token = response.data.access_token;
        refresh_token = response.data.refresh_token;
        userData = response.data.user || response.data;
      } else {
        throw new Error('Неверный формат ответа сервера');
      }
      
      const normalizedUser = normalizeUserData(userData);
      
      TokenService.setTokens(access_token, refresh_token, rememberMe);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      
      if (rememberMe && username) {
        localStorage.setItem('remembered_username', username);
      } else {
        localStorage.removeItem('remembered_username');
      }
      
      setUser(normalizedUser);
      setLoading(false);
      
      console.log('[AuthProvider] Login successful for user:', normalizedUser?.name);
      return normalizedUser;
    } catch (err) {
      console.error('[AuthProvider] Login error:', err);
      const errorMsg = err.message || 'Ошибка входа';
      setError(errorMsg);
      setLoading(false);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    console.log('[AuthProvider] Logging out user:', user?.name);
    
    try {
      await apiClient.post(API_ENDPOINTS.LOGOUT);
    } catch (err) {
      console.error('[AuthProvider] Error during logout:', err);
    } finally {
      TokenService.removeTokens();
      localStorage.removeItem('user');
      sessionStorage.clear();
      setUser(null);
      setError(null);
      setLoading(false);
    }
  }, [user]);

  const refreshUserData = useCallback(async () => {
    const token = TokenService.getAccessToken();
    if (!token) return null;
    
    try {
      console.log('[AuthProvider] Refreshing user data...');
      const userData = await apiClient.get(API_ENDPOINTS.AUTH_ME);
      const normalizedUser = normalizeUserData(userData);
      
      setUser(normalizedUser);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      return normalizedUser;
    } catch (err) {
      console.error('[AuthProvider] Failed to refresh user data:', err);
      return null;
    }
  }, []);

  const hasPermission = useCallback((permission) => {
    if (!user || !user.role) return false;
    const permissions = ROLE_PERMISSIONS[user.role] || [];
    return permissions.includes(permission);
  }, [user]);

  const hasAnyPermission = useCallback((permissionsList) => {
    if (!user || !user.role) return false;
    const userPermissions = ROLE_PERMISSIONS[user.role] || [];
    return permissionsList.some(p => userPermissions.includes(p));
  }, [user]);

  const hasRole = useCallback((role) => {
    return user?.role === role;
  }, [user]);

  const isAdmin = useCallback(() => {
    return user?.role === ROLES.ADMIN;
  }, [user]);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    logout,
    refreshUserData,
    isAuthenticated: !!user,
    userRole: user?.role || null,
    hasPermission,
    hasAnyPermission,
    hasRole,
    isAdmin,
    permissions: ROLE_PERMISSIONS[user?.role] || []
  }), [user, loading, error, login, logout, refreshUserData, hasPermission, hasAnyPermission, hasRole, isAdmin]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};